import { isAppPath } from "@paginae/blocks/app-paths";
import type { AppConfig } from "./api.ts";

/**
 * Workspaces on their own addresses (ADR-0098): what the app does differently there. Signing
 * in, Administration and the account's security (passkeys, bound to the central host) open at
 * the central address; the rest works on any address.
 */
type Where = Pick<AppConfig, "centralOrigin" | "address">;

const CENTRAL_ONLY = /^\/(admin|account\/security|sign-in|setup)(?=$|[/?#])/;

/** A path the central host serves, as a whole URL there when this is a workspace address. */
export function centralHref(where: Where, path: string): string | null {
  return where.address && CENTRAL_ONLY.test(path) ? `${where.centralOrigin}${path}` : null;
}

/** The central host's single-use pass to `url`, an address of the installation (§4). */
export const handoverHref = (centralOrigin: string, url: string) =>
  `${centralOrigin}/api/auth/handover?to=${encodeURIComponent(url)}`;

/** Leaves the app for another address: never resolves, as the page is going away. */
export function leaveFor(url: string): Promise<never> {
  window.location.assign(url);
  return new Promise<never>(() => {});
}

// ---------------------------------------------------------------------------------------------
// Readable addresses (ADR-0098 stage 2). On a workspace address every page has the address it
// has, or would have, when published (`/lectiones`, `/en/notae`), and nothing else: no ID. The
// router works with its own paths (`/w/<workspace>/p/<page>`), rewritten to and from them:
//
// - `/` is the workspace's home; the app's own screens (`/inbox`, `/w/<workspace>/settings`…)
//   keep their paths (pages never take them: `isAppPath`).
// - A page whose address is known (from the sidebar, a page loaded, a lookup) is shown at it.
// - Any other path is looked up (`/w/<workspace>/a/<path>`, router.tsx), then shown as the page.

/** This address's workspace, named by the page the sites Worker serves (sites/app.ts). */
export const addressWorkspace: string | null =
  (typeof document === "undefined"
    ? null
    : document.querySelector<HTMLMetaElement>('meta[name="paginae-workspace"]')?.content) || null;

const pathOfPage = new Map<string, string>();
const pageOfPath = new Map<string, string>();

/** A request path in stored form (decoded, lowercase, no trailing slash), or null. */
export function storedPath(pathname: string): string | null {
  try {
    const path = decodeURIComponent(pathname).normalize("NFC").toLowerCase().replace(/\/+$/, "");
    return path === "" ? "/" : path;
  } catch {
    return null;
  }
}

const encodePath = (path: string) => path.split("/").map(encodeURIComponent).join("/");

/** Notes a page's readable address, from the server. */
export function rememberAddress(pageId: string, path: string | null | undefined) {
  if (!path) return;
  const before = pathOfPage.get(pageId);
  if (before && pageOfPath.get(before) === pageId) pageOfPath.delete(before);
  pathOfPage.set(pageId, path);
  pageOfPath.set(path, pageId);
}

/** The page's readable address, if known. */
export const addressOfPage = (pageId: string) => pathOfPage.get(pageId);

/** The address bar's path as the router's (`rewrite.input`); undefined: unchanged. */
export function routerPath(url: URL, workspaceId = addressWorkspace): URL | undefined {
  if (!workspaceId) return undefined;
  const path = url.pathname;
  if (path === "/") {
    url.pathname = `/w/${workspaceId}`;
    return url;
  }
  if (isAppPath(path)) return undefined;
  const stored = storedPath(path);
  const pageId = stored ? pageOfPath.get(stored) : undefined;
  url.pathname = pageId ? `/w/${workspaceId}/p/${pageId}` : `/w/${workspaceId}/a${path}`;
  return url;
}

/** The router's path as the address bar's (`rewrite.output`); undefined: unchanged. */
export function addressBarPath(url: URL, workspaceId = addressWorkspace): URL | undefined {
  if (!workspaceId) return undefined;
  const base = `/w/${workspaceId}`;
  const path = url.pathname;
  if (path === base || path === `${base}/`) {
    url.pathname = "/";
    return url;
  }
  if (path.startsWith(`${base}/a/`)) {
    url.pathname = path.slice(base.length + 2);
    return url;
  }
  if (path.startsWith(`${base}/p/`)) {
    const id = path.slice(base.length + 3);
    const readable = id.includes("/") ? undefined : pathOfPage.get(decodeURIComponent(id));
    if (readable) {
      url.pathname = encodePath(readable);
      return url;
    }
  }
  return undefined;
}
